import {
  CognitoIdentityProviderClient,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
  DeleteUserCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoConfig } from "../config/awsConfig";
import { jwtDecode } from "jwt-decode";

const client = new CognitoIdentityProviderClient({
  region: cognitoConfig.region,
});

// Sign Up
export async function signUp(username, password, email) {
  try {
    return await client.send(
      new SignUpCommand({
        ClientId: cognitoConfig.clientId,
        Username: username,
        Password: password,
        UserAttributes: [{ Name: "email", Value: email }],
      })
    );
  } catch (error) {
    console.error("Sign Up Error:", error.name);
    throw error;
  }
}

// Confirm Sign Up
export async function confirmSignUp(username, code) {
  try {
    return await client.send(
      new ConfirmSignUpCommand({
        ClientId: cognitoConfig.clientId,
        Username: username,
        ConfirmationCode: code,
      })
    );
  } catch (error) {
    console.error("Confirm Sign Up Error:", error.name);
    throw error;
  }
}

export async function resendSignUp(username) {
  try {
    return await client.send(
      new ResendConfirmationCodeCommand({
        ClientId: cognitoConfig.clientId,
        Username: username,
      })
    );
  } catch (error) {
    console.error("Resend Confirmation Code Error:", error.name);
    throw error;
  }
}

// Extract a normalized token bundle from an AuthenticationResult.
function toTokenBundle(authenticationResult, fallbackRefreshToken = null) {
  const { IdToken, AccessToken, RefreshToken, ExpiresIn } = authenticationResult;

  // Cognito omits RefreshToken on refresh-token auth; keep the existing one.
  const effectiveRefreshToken = RefreshToken || fallbackRefreshToken;

  if (!IdToken || !AccessToken || !effectiveRefreshToken) {
    throw new Error("Authentication failed: missing required tokens");
  }

  const tokenExpiry = Date.now() + (ExpiresIn || 3600) * 1000;
  const email = jwtDecode(IdToken).email;

  return {
    IdToken,
    AccessToken,
    RefreshToken: effectiveRefreshToken,
    ExpiresIn,
    tokenExpiry,
    email,
  };
}

// Sign In
export async function signIn(username, password) {
  try {
    const response = await client.send(
      new InitiateAuthCommand({
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: cognitoConfig.clientId,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      })
    );

    if (response.ChallengeName) {
      return handleAuthChallenge(response, username);
    }

    if (!response.AuthenticationResult) {
      throw new Error("Authentication failed: invalid response format");
    }

    // Token storage is handled by the caller (AuthContext via TokenManager).
    return toTokenBundle(response.AuthenticationResult);
  } catch (error) {
    console.error("Sign In Error:", error.name || error.message);

    if (error.name === "NotAuthorizedException") {
      throw new Error("Invalid email or password");
    } else if (error.name === "UserNotConfirmedException") {
      throw new Error(
        "Account not confirmed. Please check your email for confirmation instructions."
      );
    } else if (error.name === "PasswordResetRequiredException") {
      throw new Error("Password reset required. Please reset your password.");
    } else if (error.name === "UserNotFoundException") {
      throw new Error("No account found with this email.");
    } else if (error.name === "TooManyRequestsException") {
      throw new Error("Too many failed attempts. Please try again later.");
    }

    throw error;
  }
}

// Handle authentication challenges (like MFA, password change, etc.)
function handleAuthChallenge(response, username) {
  if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    // Return challenge information so the UI can handle it
    return {
      challengeName: "NEW_PASSWORD_REQUIRED",
      session: response.Session,
      username,
      challengeParameters: response.ChallengeParameters,
      message: "New password required. Please set a new password to continue.",
    };
  } else if (response.ChallengeName === "MFA_SETUP") {
    throw new Error("MFA setup required. Please contact support.");
  } else if (response.ChallengeName === "SOFTWARE_TOKEN_MFA") {
    throw new Error("MFA token required. This feature is not yet implemented.");
  }

  throw new Error(`Unsupported authentication challenge: ${response.ChallengeName}`);
}

// Refresh Token
export async function refreshTokenFunc(refreshTokenValue) {
  try {
    const response = await client.send(
      new InitiateAuthCommand({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: cognitoConfig.clientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshTokenValue,
        },
      })
    );

    // Storage is handled by TokenManager, which owns the refresh lifecycle.
    return toTokenBundle(response.AuthenticationResult, refreshTokenValue);
  } catch (error) {
    console.error("Failed to refresh token:", error.name || error.message);
    // Keep the SDK's error name as a property, not only inside the message:
    // TokenManager tells "Cognito rejected the refresh token"
    // (NotAuthorizedException) from "the network failed" by it.
    const refreshError = new Error(`Failed to refresh token: ${error.name || error.message}`);
    refreshError.name = error.name || refreshError.name;
    throw refreshError;
  }
}

// Password Reset Functions
export async function forgotPassword(username) {
  try {
    return await client.send(
      new ForgotPasswordCommand({
        ClientId: cognitoConfig.clientId,
        Username: username,
      })
    );
  } catch (error) {
    console.error("Forgot Password Error:", error.name);
    throw error;
  }
}

export async function confirmForgotPassword(username, confirmationCode, newPassword) {
  try {
    return await client.send(
      new ConfirmForgotPasswordCommand({
        ClientId: cognitoConfig.clientId,
        Username: username,
        ConfirmationCode: confirmationCode,
        Password: newPassword,
      })
    );
  } catch (error) {
    console.error("Confirm Forgot Password Error:", error.name);
    throw error;
  }
}

// Handle NEW_PASSWORD_REQUIRED challenge
export async function respondToNewPasswordChallenge(username, newPassword, session) {
  try {
    const response = await client.send(
      new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.clientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        Session: session,
        ChallengeResponses: {
          USERNAME: username,
          NEW_PASSWORD: newPassword,
        },
      })
    );

    if (response.AuthenticationResult) {
      return toTokenBundle(response.AuthenticationResult);
    }

    return response;
  } catch (error) {
    console.error("New Password Challenge Error:", error.name);
    throw error;
  }
}

// Change Password (for authenticated users)
export async function changePassword(accessToken, previousPassword, proposedPassword) {
  try {
    return await client.send(
      new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: previousPassword,
        ProposedPassword: proposedPassword,
      })
    );
  } catch (error) {
    console.error("Change Password Error:", error.name);
    throw error;
  }
}

// Permanently delete the authenticated user's Cognito account
export async function deleteUser(accessToken) {
  try {
    return await client.send(new DeleteUserCommand({ AccessToken: accessToken }));
  } catch (error) {
    console.error("Delete User Error:", error.name);
    throw error;
  }
}
